import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { History, Pencil } from "lucide-react";
import PaginatorCst from "@components/PaginatorCst";
import SearchBar from "@components/SearchableMultiSelectListCst/SearchBar";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import {
  PermissionGate,
  PERMISSIONS,
  usePermissions,
} from "@utils/permissions";
import UploadModalCst from "@components/UploadModalCst";
import { useFetchReasonCodes } from "@pages/Contents/Corporate/EditionsPhysical/hooks/useFetchReasonCodes";
import { useEditionRegistry } from "../hooks/useEditionRegistry";
import { useRegistryFile } from "../hooks/useRegistryFile";
import { EditionRegistryRow } from "../types";
import RegistryHistoryModal from "./RegistryHistoryModal";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

interface RegistryTabProps {
  corporateId: string | number;
  editionId: string | number;
}

const REGISTRY_PAGE_SIZE = 10;
//TODO Ricerca disabilitata finche' l'API di ricerca non e' pronta si usano i filtri
// canonici. Rimettere a true per riattivare la barra.
const REGISTRY_SEARCH_ENABLED = false;

const REGISTRY_ACCEPT =
  ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

const formatDate = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === "") return "-";
  const date = dayjs(
    typeof value === "number" && value < 1e12 ? value * 1000 : value,
  );
  if (!date.isValid()) return "-";
  return date.locale("it").format("DD/MM/YYYY HH:mm");
};

const formatPercentage = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === "") return "-";
  return `${value}%`;
};

const formatCell = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

interface RegistryColumn {
  key: keyof EditionRegistryRow;
  render?: (row: EditionRegistryRow) => string;
}

const RegistryTab: React.FC<RegistryTabProps> = ({
  corporateId,
  editionId,
}) => {
  // D09 (decisione: allineare V2 a v1): Modifica registro e Cronologia con
  // virtualregister.download, caricamento del file con virtualregister.upload
  // (virtual-register-config.json di v1).
  const { can } = usePermissions();
  const canUploadRegistry = can(PERMISSIONS.registry.upload);
  const { t, i18n } = useTranslation("edition-detail");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(REGISTRY_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const {
    items: rows,
    totalItems,
    totalPages,
    isLoading,
    isUpdating,
    mutate: refetchRegistry,
  } = useEditionRegistry({
    corporateId,
    editionId,
    page,
    pageSize,
    search: search || undefined,
    fieldFilters,
  });

  const [isRegistryReady, setIsRegistryReady] = useState(false);

  useEffect(() => {
    if (!isUpdating && !isLoading) setIsRegistryReady(true);
  }, [isUpdating, isLoading]);

  const { uploadRegistryFile, downloadRegistryFile } = useRegistryFile(
    corporateId,
    editionId,
  );

  const { formattedOptions: absenceReasonOptions } = useFetchReasonCodes(
    String(corporateId),
    "ABSENCE",
  );

  const absenceReasonFilterOptions = useMemo(
    () =>
      (absenceReasonOptions || []).map(
        (o: { value: string; label: { it?: string; en?: string } }) => ({
          value: o.value,
          label:
            (i18n.language === "it" ? o.label?.it : o.label?.en) ||
            o.label?.en ||
            o.value,
        }),
      ),
    [absenceReasonOptions, i18n.language],
  );

  const formatAbsenceReason = useCallback(
    (value?: number | string | null): string => {
      if (value === null || value === undefined || value === "") return "-";
      const option = absenceReasonOptions.find(
        (o: { value: string }) => o.value === value,
      );
      if (!option) return String(value);
      const lang = i18n.language === "it" ? "it" : "en";
      return option.label[lang] || option.label.en || String(value);
    },
    [absenceReasonOptions, i18n.language],
  );

  const COLUMNS = useMemo<RegistryColumn[]>(
    () => [
      { key: "userId" },
      { key: "externalId" },
      { key: "firstName" },
      { key: "lastName" },
      { key: "email" },
      {
        key: "roles",
        render: (r) =>
          r.roles
            ? t(`input:options.meeting_all_role.${r.roles.toLowerCase()}`, {
                defaultValue: r.roles,
              })
            : "-",
      },
      {
        key: "editionEnrollmentDate",
        render: (r) => formatDate(r.editionEnrollmentDate),
      },
      {
        key: "lastProcessingDate",
        render: (r) => formatDate(r.lastProcessingDate),
      },
      {
        key: "participationPercentage",
        render: (r) => formatPercentage(r.participationPercentage),
      },
      { key: "totalDuration" },
      {
        key: "processingStatus",
        render: (r) =>
          r.processingStatus
            ? t(`register.processing-status.${r.processingStatus}`, {
                defaultValue: r.processingStatus,
              })
            : "-",
      },
      {
        key: "participationProgressionStatus",
        render: (r) =>
          r.participationProgressionStatus
            ? t(
                `register.progression-status.${r.participationProgressionStatus}`,
                { defaultValue: r.participationProgressionStatus },
              )
            : "-",
      },
      {
        key: "absenceReason",
        render: (r) => formatAbsenceReason(r.absenceReason),
      },
      { key: "testResult" },
    ],
    [formatAbsenceReason, t],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {t("register.title")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate
            permission={PERMISSIONS.registry.download}
            mode="disable"
          >
            <ButtonTertiary
              size="md"
              startIcon={<Pencil size={16} />}
              onClick={() => setUploadOpen(true)}
              disabled={!isRegistryReady}
            >
              {t("register.edit")}
            </ButtonTertiary>
          </PermissionGate>
          <PermissionGate
            permission={PERMISSIONS.registry.download}
            mode="disable"
          >
            <ButtonTertiary
              size="md"
              startIcon={<History size={16} />}
              onClick={() => setHistoryOpen(true)}
              disabled={!isRegistryReady}
            >
              {t("register.history")}
            </ButtonTertiary>
          </PermissionGate>
        </Box>
      </Box>

      {REGISTRY_SEARCH_ENABLED && (
        <SearchBar
          initialValue={search}
          onSubmit={(value: string) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder={String(t("register.search-placeholder"))}
          buttonLabel={String(t("register.search-placeholder"))}
        />
      )}

      <FiltersAccordionToggle
        pageId={PagesID["corporate.virtual-register"]}
        value={fieldFilters}
        onApply={(next) => {
          setFieldFilters(next);
          setPage(1);
        }}
        dynamicOptions={{ absenceReason: absenceReasonFilterOptions }}
      />

      {isUpdating ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            py: 6,
            color: theme.palette.text.primary,
          }}
        >
          <CircularProgress />
          <Typography sx={{ fontSize: "13px" }}>
            {t("register.updating")}
          </Typography>
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 6,
            color: theme.palette.text.primary,
            fontSize: "13px",
          }}
        >
          <Typography>{t("register.empty")}</Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            borderRadius: "12px",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                    }}
                  >
                    {t(`register.columns.${col.key}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${row.userId ?? "row"}-${index}`} hover>
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {col.render ? col.render(row) : formatCell(row[col.key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isUpdating && !isLoading && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </Box>
      )}

      {uploadOpen && (
        <UploadModalCst
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          title={String(t("register.upload-modal.title"))}
          accept={REGISTRY_ACCEPT}
          banner={{
            title: String(t("register.upload-modal.banner-title")),
            text: String(t("register.upload-modal.banner-text")),
            downloadLabel: String(t("register.upload-modal.download")),
            onDownload: downloadRegistryFile,
          }}
          onUpload={uploadRegistryFile}
          uploadDisabled={!canUploadRegistry}
          onUploaded={refetchRegistry}
          successMessage={String(t("register.upload-modal.success"))}
        />
      )}

      {historyOpen && (
        <RegistryHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          corporateId={corporateId}
          editionId={editionId}
        />
      )}
    </Box>
  );
};

export default RegistryTab;
